import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

// Shared "you'll lose your work if you leave" guard for the workflow tools.
//
// This logic is currently copy-pasted across nine components (Import Workflow's ChatUI, Export
// Workflow's EARChatUI, Beneficial Ownership, Customs Entry Analyzer, Tariff, Classification,
// MergeTariff, AIReportBuilder, CreateImportDatabaseDialog) — CustomsEntryAnalyzer.tsx even
// notes "No shared hook exists for this in the codebase; each tool keeps its own copy." This
// is that hook, lifted from ChatUI (the cleanest copy). It is deliberately introduced WITHOUT
// migrating the existing nine: each is a shipped, separately-tested tool, and rewiring them all
// is its own piece of work. New tools should use this; the others can move over incrementally.
//
// Two independent mechanisms, because they catch different things:
//   * react-router v5 <Prompt> — in-app navigation, including sidebar links (they're <Link>s).
//     Gives the styled dialog.
//   * beforeunload — hard refresh, tab close, back out of the SPA. Native browser dialog only;
//     the styled one cannot be shown there.

interface UseLeaveGuardOptions {
  /** localStorage key for the per-user "don't show again" opt-out. */
  storageKey: string;
  /** True when there is unsaved work worth warning about. The opt-out is applied on top. */
  shouldWarn: boolean;
  /** Optional teardown run just before navigating away (e.g. clearing session state). */
  onLeave?: () => void;
}

// The per-user "don't show again" opt-out, also used directly by tools that confirm a single
// action with LeaveGuardConfirmDialog rather than guarding every exit.
export const hasOptedOutOfLeaveWarning = (storageKey: string): boolean => {
  try {
    return localStorage.getItem(storageKey) === 'true';
  } catch {
    // Private browsing / blocked storage — warn rather than silently stop warning.
    return false;
  }
};

export const optOutOfLeaveWarning = (storageKey: string): void => {
  try {
    localStorage.setItem(storageKey, 'true');
  } catch {
    // Couldn't persist the preference; callers still honour it for this session.
  }
};

export interface LeaveGuard {
  isLeaveModalOpen: boolean;
  /** Pass to <Prompt when={...} />. False once the user has opted out. */
  shouldBlockNavigation: boolean;
  /** Pass to <Prompt message={...} />. Returns false to block and open the dialog. */
  handleBlockedNavigation: (nextLocation: any, action?: string) => boolean;
  /** Dismiss the dialog and stay put. */
  closeLeaveModal: () => void;
  /** Hide the dialog while a save flow is in progress, retaining the blocked destination. */
  beginSaveBeforeLeaving: () => void;
  /** Continue to the blocked destination after a successful save. */
  handleLeaveAfterSaving: () => void;
  handleLeaveWithoutSaving: () => void;
  handleLeaveAndOptOut: () => void;
}

export const useLeaveGuard = ({
  storageKey,
  shouldWarn,
  onLeave,
}: UseLeaveGuardOptions): LeaveGuard => {
  const history = useHistory();
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const [pendingLocation, setPendingLocation] = useState<any>(null);
  const [pendingAction, setPendingAction] = useState<string | undefined>(undefined);
  const [hasOptedOut, setHasOptedOut] = useState(false);
  // Set for exactly one navigation, so our own history.push isn't blocked by the same <Prompt>
  // that just stopped the user's click.
  const allowNavigationRef = useRef(false);
  // Read through a ref so a caller passing a fresh closure each render doesn't re-run effects.
  const onLeaveRef = useRef(onLeave);
  onLeaveRef.current = onLeave;

  useEffect(() => {
    setHasOptedOut(hasOptedOutOfLeaveWarning(storageKey));
  }, [storageKey]);

  const shouldBlockNavigation = shouldWarn && !hasOptedOut;

  useEffect(() => {
    if (!shouldBlockNavigation) {
      return undefined;
    }
    const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnloadHandler);
    return () => window.removeEventListener('beforeunload', beforeUnloadHandler);
  }, [shouldBlockNavigation]);

  const continueToPendingLocation = useCallback(() => {
    if (!pendingLocation) {
      return;
    }
    allowNavigationRef.current = true;
    const target = {
      pathname: pendingLocation.pathname,
      search: pendingLocation.search,
      hash: pendingLocation.hash,
      state: pendingLocation.state,
    };
    // A blocked redirect replays as a replace so it doesn't add a history entry.
    // A blocked Back/Forward (POP) stays a push: history v4 doesn't say which way
    // it went, and after a reload it can't revert the POP at all (the URL is
    // already the target), so goBack() could overshoot, even out of the app.
    if (pendingAction === 'REPLACE') {
      history.replace(target);
    } else {
      history.push(target);
    }
    window.setTimeout(() => {
      allowNavigationRef.current = false;
      setPendingLocation(null);
    }, 0);
  }, [history, pendingAction, pendingLocation]);

  const handleBlockedNavigation = useCallback(
    (nextLocation: any, action?: string) => {
      if (allowNavigationRef.current || !shouldBlockNavigation) {
        return true;
      }
      setPendingLocation(nextLocation);
      setPendingAction(action);
      setIsLeaveModalOpen(true);
      return false;
    },
    [shouldBlockNavigation],
  );

  const closeLeaveModal = useCallback(() => {
    setIsLeaveModalOpen(false);
    setPendingLocation(null);
  }, []);

  const beginSaveBeforeLeaving = useCallback(() => {
    setIsLeaveModalOpen(false);
  }, []);

  const handleLeaveAfterSaving = useCallback(() => {
    setIsLeaveModalOpen(false);
    onLeaveRef.current?.();
    continueToPendingLocation();
  }, [continueToPendingLocation]);

  const handleLeaveWithoutSaving = useCallback(() => {
    setIsLeaveModalOpen(false);
    onLeaveRef.current?.();
    continueToPendingLocation();
  }, [continueToPendingLocation]);

  const handleLeaveAndOptOut = useCallback(() => {
    optOutOfLeaveWarning(storageKey);
    setHasOptedOut(true);
    handleLeaveWithoutSaving();
  }, [handleLeaveWithoutSaving, storageKey]);

  return useMemo(
    () => ({
      isLeaveModalOpen: isLeaveModalOpen,
      shouldBlockNavigation: shouldBlockNavigation,
      handleBlockedNavigation: handleBlockedNavigation,
      closeLeaveModal: closeLeaveModal,
      beginSaveBeforeLeaving: beginSaveBeforeLeaving,
      handleLeaveAfterSaving: handleLeaveAfterSaving,
      handleLeaveWithoutSaving: handleLeaveWithoutSaving,
      handleLeaveAndOptOut: handleLeaveAndOptOut,
    }),
    [
      closeLeaveModal,
      beginSaveBeforeLeaving,
      handleBlockedNavigation,
      handleLeaveAndOptOut,
      handleLeaveAfterSaving,
      handleLeaveWithoutSaving,
      isLeaveModalOpen,
      shouldBlockNavigation,
    ],
  );
};

export default useLeaveGuard;
